import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DictationAccuracySettings, DictationCaptureSettings, DictationLanguageSettings, } from '@/components/settings/reporting/DictationVocabularySettings';
import { useReportSettings } from '@/components/settings/reporting/useReportSettings';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAudioInputDevices } from '@/hooks/useAudioInputDevices';
import { useViewerSettings } from '@/hooks/useViewerSettings';
import { useFeatureFlags } from '@/lib/feature-flags/useFeatureFlag';
import { Mic, MicOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
export function MicrophoneTab() {
    const { t } = useTranslation('settings');
    const { settings, updateAudioInput } = useViewerSettings();
    const { devices, permission, loading, requestPermission } = useAudioInputDevices();
    const { settings: reportSettings } = useReportSettings();
    const external = reportSettings.dictationEngine === 'external';
    // Loading state matters: the flag reads false until the fetch lands, and a
    // banner that flashes on every open would read as a real outage.
    const { flags, isLoading: flagsLoading } = useFeatureFlags();
    const dictationFlagOff = !flagsLoading && flags.dictation !== true;
    const flagBanner = dictationFlagOff && (_jsx("div", { role: "status", className: "border-border bg-muted/40 text-muted-foreground rounded-lg border px-3 py-2 text-xs", children: t('microphone.notEnabled') }));
    const selectedDeviceId = settings.audioInput?.deviceId ?? '';
    const [testing, setTesting] = useState(false);
    const [level, setLevel] = useState(0);
    // Live input-level meter: opens the selected mic, samples RMS via an
    // AnalyserNode, and renders a bar so the user can confirm the mic works.
    const cleanupRef = useRef(null);
    useEffect(() => {
        if (!testing)
            return;
        let cancelled = false;
        let raf = 0;
        const run = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true,
                });
                if (cancelled) {
                    stream.getTracks().forEach((tr) => tr.stop());
                    return;
                }
                const AudioCtx = window.AudioContext ||
                    window.webkitAudioContext;
                const ctx = new AudioCtx();
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 512;
                source.connect(analyser);
                const data = new Uint8Array(analyser.frequencyBinCount);
                const tick = () => {
                    analyser.getByteTimeDomainData(data);
                    let sum = 0;
                    for (let i = 0; i < data.length; i++) {
                        const v = (data[i] - 128) / 128;
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / data.length);
                    setLevel(Math.min(100, Math.round(rms * 250)));
                    raf = requestAnimationFrame(tick);
                };
                tick();
                cleanupRef.current = () => {
                    cancelAnimationFrame(raf);
                    stream.getTracks().forEach((tr) => tr.stop());
                    void ctx.close();
                };
            }
            catch {
                if (!cancelled)
                    setTesting(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
            cleanupRef.current?.();
            cleanupRef.current = null;
        };
    }, [testing, selectedDeviceId]);
    if (permission === 'unsupported') {
        return (_jsxs("div", { className: "space-y-4", children: [flagBanner, _jsx("p", { className: "text-muted-foreground text-sm", children: t('microphone.description') }), _jsx("div", { className: "bg-muted/30 text-muted-foreground rounded-lg p-4 text-sm", children: t('microphone.unsupported') })] }));
    }
    // Compact mic row first: pick the device, prove it works, move on. It heads
    // the "How you dictate" section, which owns the rest of that story.
    const micRow = (_jsxs("div", { className: "bg-muted/30 space-y-2 rounded-lg px-4 py-3", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Mic, { size: 16, className: "shrink-0 text-[#3FB4A8]" }), _jsx(Label, { htmlFor: "microphone-device", className: "text-foreground", children: t('microphone.device') }), _jsxs("select", { id: "microphone-device", value: selectedDeviceId, onChange: (e) => updateAudioInput({ deviceId: e.target.value || null }), disabled: permission !== 'granted', className: "border-border bg-background text-foreground min-w-0 flex-1 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 sm:max-w-64", children: [_jsx("option", { value: "", children: t('microphone.systemDefault') }), devices.map((d) => (_jsx("option", { value: d.deviceId, children: d.label || t('microphone.unnamedDevice', { defaultValue: 'Unnamed microphone' }) }, d.deviceId)))] }), permission === 'denied' ? null : permission !== 'granted' ? (_jsxs(Button, { variant: "outline", size: "sm", onClick: () => void requestPermission(), disabled: loading, children: [_jsx(Mic, { size: 14, className: "mr-1.5" }), t('microphone.enableAccess')] })) : external ? null : (_jsx(Button, { variant: "outline", size: "sm", onClick: () => setTesting((v) => !v), children: testing ? (_jsxs(_Fragment, { children: [_jsx(MicOff, { size: 14, className: "mr-1.5" }), t('microphone.stopTest')] })) : (_jsxs(_Fragment, { children: [_jsx(Mic, { size: 14, className: "mr-1.5" }), t('microphone.startTest')] })) }))] }), permission === 'denied' && (_jsxs("p", { role: "status", className: "text-muted-foreground text-xs", children: [_jsx("span", { className: "text-foreground font-medium", children: t('microphone.blocked') }), ' ', t('microphone.blockedHow')] })), external && _jsx("p", { className: "text-muted-foreground text-xs", children: t('microphone.externalNote') }), testing && permission === 'granted' && !external && (_jsx("div", { className: "bg-muted h-2 w-full overflow-hidden rounded", children: _jsx("div", { className: "h-full bg-[#3FB4A8] transition-[width] duration-75", style: { width: `${level}%` } }) }))] }));
    return (_jsxs("div", { className: "space-y-6", children: [flagBanner, _jsx(DictationCaptureSettings, { children: micRow }), _jsx(DictationLanguageSettings, {}), _jsx(DictationAccuracySettings, {})] }));
}
